// The Fronts view (round 5 frames f1a and f1b; build notes, choices 3 and 7): one wall's doors and drawer fronts drawn
// by ZeroGap from the resolved sizes and the reveals each box reads, never from a loaded drawing. The boxes sit side
// by side in the order of their marks, each at its own width (uppers on a row of their own above the bases); each
// front carries its number and size; every gap is labelled, a gap held off the job standard in gold with the standard
// beside it. Under the drawing, the gaps off the standard with one Approve each: a gap at a joint is two boxes'
// reveals, and one approval writes both.

import { formatLength, frontsLayout, itemDeviations, type FrontsLayout, type Gap, type ResolvedItem } from '@zerogap/engine';
import type { Row } from '../../../lib/api';
import { useSession } from '../../../lib/session';
import type { JobCtx } from '../Workspace';
import { approveChange, mmdd, unapproveChange } from './ItemPanel';
import { wallShort, type WalkModel } from './model';

const frac = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v === 0 ? '0' : formatLength(String(v)));

interface Row2 {
  layout: FrontsLayout;
  /** Gaps with a joint's two edge gaps folded into it. */
  gaps: Gap[];
}

/** The wall's boxes that carry fronts (doors, drawer fronts, fixed panels), base items under the open build, in rows: uppers above, bases below. */
function rowsFor(ctx: JobCtx, model: WalkModel, wallId: string): Row2[] {
  const res = model.resOf(model.res.items[0]?.id ?? '');
  const lib = ctx.r.lib!;
  const wall = model.walls.find((w) => w.view?.id === wallId);
  const marks = ctx.data.marks.filter((m) => m.view_id === wallId) as any[];
  // what a box makes that is not a front (an applied end panel, a drawer box) stays out of the Fronts view
  const notFront = (i: ResolvedItem) => !!i.parent_item_id && /end panel|side panel|drawer box|toe kick|shelf|back panel/i.test(`${i.catalog_item_name ?? ''} ${i.name}`);
  const fres = { ...res, items: res.items.filter((i) => !notFront(i)) };
  const kidsOf = (id: string) => fres.items.filter((i) => i.parent_item_id === id);
  const sized = (i: ResolvedItem) => ['width', 'height'].every((k) => {
    const v = i.inputs.find((x) => x.key === k && !x.hidden)?.value;
    return v !== null && v !== undefined && v !== '';
  });
  const boxes = (wall?.stops ?? [])
    .map((s) => s.item)
    .filter((i): i is ResolvedItem => !!i && !i.add_on_id && !model.notIn(i) && res.items.some((x) => x.id === i.id))
    .filter((i) => kidsOf(i.id).some(sized));
  const yOf = (i: ResolvedItem) => Number(marks.find((m) => m.job_item_id === i.id)?.y_pct ?? 100);
  const upper = boxes.filter((b) => yOf(b) < 45);
  const lower = boxes.filter((b) => yOf(b) >= 45);
  return [upper, lower]
    .filter((list) => list.length > 0)
    .map((list) => {
      const layout = frontsLayout(fres, lib, list, marks);
      const joints = layout.gaps.filter((g) => g.key.startsWith('joint:'));
      const folded = new Set<string>();
      for (const j of joints) {
        folded.add(`${j.item_ids[0]}:right`);
        folded.add(`${j.item_ids[1]}:left`);
      }
      return { layout, gaps: layout.gaps.filter((g) => !folded.has(g.key)) };
    });
}

/** The deviations a gap's keys name, as they stand on each item now (an approval holds while the value does). */
function gapDeviations(ctx: JobCtx, model: WalkModel, g: Gap) {
  return g.keys.map((k) => {
    const item = model.res.items.find((i) => i.id === k.item_id)!;
    const d = item ? itemDeviations(item, model.resOf(item.id), ctx.r.lib!, model.rowOf(item.id) as any).find((x) => x.key === k.key) ?? null : null;
    return { item, key: k.key, value: d ? d.raw : k.value, d };
  });
}

export function FrontsView({ ctx, model, wallId, selectedId, onPick, users }: { ctx: JobCtx; model: WalkModel; wallId: string | null; selectedId: string | null; onPick: (id: string) => void; users: Row[] }) {
  const s = useSession();
  const canEdit = s.canEdit('screen.item') && !ctx.locked;
  if (!wallId || wallId === 'room') return <div className="roomnote"><b>Whole room has no fronts to draw</b><span>Pick a wall: its doors and drawer fronts are drawn from their resolved sizes.</span></div>;
  const rows = rowsFor(ctx, model, wallId);
  const std = model.res.standards;
  const lib = ctx.r.lib!;
  const label = (key: string) => lib.standards.find((d) => d.key === key)?.label ?? key;
  const held = rows.flatMap((r) => r.gaps.filter((g) => g.held));
  const wall = model.walls.find((w) => w.view?.id === wallId);

  return (
    <>
      {rows.length === 0 ? (
        <div className="roomnote">
          <b>No doors or drawer fronts on {wallShort(wall?.name)}</b>
          <span>The Fronts view draws the boxes on this wall that make doors, drawer fronts or fixed panels, under the open build.</span>
        </div>
      ) : (
        <div className="draw fronts">
          <FrontsSvg rows={rows} selectedId={selectedId} onPick={onPick} name={wall?.name ?? 'wall'} />
        </div>
      )}
      <div className="dnote">Fronts at their resolved sizes; gaps are to scale, so read the labels. Gold: held off the job standard. Click a front to open it.</div>
      <div className="gaps">
        <h4>Gaps off the job standard · {held.length}</h4>
        {held.length === 0 && <p className="only">Every gap on this wall reads the job standard.</p>}
        {held.map((g) => {
          const parts = gapDeviations(ctx, model, g);
          const ok = parts.length > 0 && parts.every((x) => x.d?.approved);
          const joint = g.item_ids.length > 1;
          const nums = parts.map((x) => x.item?.number ?? '');
          const each = parts.map((x) => x.d?.standard.replace(/^standard /, '') ?? '').filter(Boolean);
          const first = parts.find((x) => x.d?.approved)?.d?.approved;
          const value = parts.map((x) => frac(Number(x.value))).filter((v, i, a) => a.indexOf(v) === i).join(' / ');
          return (
            <div key={g.key} className={`gaprow${ok ? ' ok' : ''}`}>
              <div className="v">
                {joint ? `${nums.join(' | ')} joint · ` : `${nums[0]} · `}
                {g.label.split(' · ')[0]}
                <small>
                  {parts.map((x, i) => (
                    <span key={x.key}>
                      {i > 0 ? ' and ' : ''}
                      {x.item?.number} {label(x.key.replace(/^std:/, ''))} <span className="held">{frac(Number(x.value))}</span>
                    </span>
                  ))}
                  {each.length ? ` · job standard ${each[0]}${joint ? ' each' : ''}` : ''}
                  {joint ? ' · one approval covers both' : ''}
                </small>
              </div>
              <div className="mini">
                {ok ? (
                  <>
                    <span className="st">
                      Approved {mmdd(first?.at)} · {users.find((u) => u.id === first?.by)?.name ?? 'Spencer'}
                    </span>
                    {canEdit && (
                      <button type="button" className="btn" onClick={() => void Promise.all(parts.map((x) => unapproveChange(x.item, x.key)))}>
                        Undo
                      </button>
                    )}
                  </>
                ) : (
                  canEdit && (
                    <button
                      type="button"
                      className="btn pri"
                      onClick={async () => {
                        for (const x of parts) if (!x.d?.approved) await approveChange(ctx, model.resOf(x.item.id).items, x.item, x.key, x.value);
                      }}
                    >
                      Approve {value}
                      {joint ? ' on both' : ''}
                    </button>
                  )
                )}
              </div>
            </div>
          );
        })}
        <div className="gstd">
          <span>Job standards this wall reads:</span>
          {(['left', 'right', 'between', 'top', 'bottom'] as const).map((k) => (
            <span key={k}>
              {k} <b>{frac(std[`reveal_${k}`] === undefined || std[`reveal_${k}`] === null ? null : Number(std[`reveal_${k}`]))}</b>
            </span>
          ))}
          <span className="only">set on Job setup</span>
        </div>
      </div>
    </>
  );
}

/** A joint's standard as its two reveals: 1/8 + 1/8. */
function jointStd(L: FrontsLayout, g: Gap): string | null {
  if (g.item_ids.length < 2) return null;
  const a = L.boxes.find((b) => b.item.id === g.item_ids[0]);
  const b = L.boxes.find((x) => x.item.id === g.item_ids[1]);
  if (!a || !b) return null;
  return `${frac(a.reveals.right.standard)} + ${frac(b.reveals.left.standard)}`;
}

function FrontsSvg({ rows, selectedId, onPick, name }: { rows: Row2[]; selectedId: string | null; onPick: (id: string) => void; name: string }) {
  const widest = Math.max(...rows.map((r) => r.layout.width), 1);
  const sc = Math.min(9.55, 720 / widest);
  const PL = 34;
  const PR = 76;
  const TOP = 78;
  const UNDER = 40;
  let y0 = 0;
  const placed = rows.map((r) => {
    const top = y0 + TOP;
    const h = r.layout.height * sc;
    y0 = top + h + UNDER;
    return { r, top, h };
  });
  const vw = PL + widest * sc + PR;
  const vh = y0 + 6;
  return (
    <svg viewBox={`0 0 ${vw} ${vh}`} role="img" aria-label={`${name}: fronts drawn to scale with every reveal labelled`}>
      {placed.map(({ r, top, h }, ri) => {
        const L = r.layout;
        const X = (x: number) => PL + x * sc;
        const Y = (y: number) => top + h - y * sc; // y in inches from the row's floor line up
        // a base stands on its toe kick: its face (the edge the reveals read from) starts above it
        const tkOf = (b: (typeof L.boxes)[number]) => {
          const v = b.item.inputs.find((i) => i.key === 'toe_kick_height')?.value ?? b.item.standards_used.find((x) => x.key === 'toe_kick_height')?.value ?? null;
          const tk = Number(v ?? 0);
          if (!Number.isFinite(tk) || tk <= 0) return 0;
          const top = Math.max(0, ...b.fronts.map((f) => f.y + f.height)) + (b.reveals.top.value ?? 0);
          return top + tk <= b.height + 0.01 ? tk : 0;
        };
        return (
          <g key={ri}>
            <rect x={X(0)} y={top} width={L.width * sc} height={h} fill="var(--wall)" />
            <line x1={X(0) - 12} y1={Y(0)} x2={X(L.width) + 12} y2={Y(0)} stroke="var(--fg)" strokeWidth={2} />
            {L.boxes.map((b) => {
              const tk = tkOf(b);
              return (
                <g key={b.item.id}>
                  <rect x={X(b.x)} y={Y(b.height)} width={b.width * sc} height={(b.height - tk) * sc} fill="var(--card-2)" stroke="var(--fg-2)" strokeWidth={1.2} />
                  {tk > 0 && <rect x={X(b.x)} y={Y(tk)} width={b.width * sc} height={tk * sc} fill="var(--kick)" stroke="var(--fg-2)" strokeWidth={1} />}
                </g>
              );
            })}
            {L.boxes.flatMap((b) => {
              const doors = b.fronts.filter((f) => f.kind !== 'drawer');
              return b.fronts.map((f) => {
                const on = selectedId === f.item.id;
                const di = doors.indexOf(f);
                const heldHere =
                  b.reveals.top.held || b.reveals.bottom.held || (f.kind !== 'drawer' && ((di === 0 && b.reveals.left.held) || (di === doors.length - 1 && b.reveals.right.held) || (doors.length > 1 && b.reveals.between.held)));
                const tk = tkOf(b);
                const x = X(b.x + f.x);
                const y = Y(tk + f.y + f.height);
                const w = f.width * sc;
                const fh = f.height * sc;
                const cx = x + w / 2;
                const cy = y + fh / 2;
                return (
                  <g
                    key={f.item.id}
                    role="button"
                    tabIndex={0}
                    aria-label={`${f.item.number} ${frac(f.width)} × ${frac(f.height)}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onPick(f.item.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onPick(f.item.id);
                      }
                    }}
                  >
                    <rect x={x} y={y} width={w} height={fh} fill={on ? 'var(--sel)' : 'var(--front)'} stroke={on ? 'var(--accent)' : 'var(--front-line)'} strokeWidth={on ? 2.5 : 1.1} strokeDasharray={f.kind === 'panel' && !/door/i.test(f.item.name) ? '3 3' : undefined} />
                    <text x={cx} y={cy - 4} textAnchor="middle" fontSize={12.5} fontWeight={700} fontFamily="IBM Plex Mono, monospace" fill={on ? 'var(--accent)' : 'var(--fg)'}>
                      {f.item.number}
                    </text>
                    <text x={cx} y={cy + 13} textAnchor="middle" fontSize={10.5} fill="var(--fg-2)">
                      {f.kind === 'panel' && !/door/i.test(f.item.name) ? 'panel · ' : ''}
                      {frac(f.width)} × {frac(f.height)}
                    </text>
                    {heldHere && (
                      <>
                        <rect x={cx - 19} y={y + 8} width={38} height={15} rx={3} fill="var(--chg)" />
                        <text x={cx} y={y + 19} textAnchor="middle" fontSize={9.5} fontWeight={700} fill="var(--chg-ink)">
                          held
                        </text>
                      </>
                    )}
                  </g>
                );
              });
            })}
            {r.gaps.map((g, gi) => {
              const box = L.boxes.find((b) => b.item.id === g.item_ids[0]);
              const fr = box?.fronts ?? [];
              const tk = box ? tkOf(box) : 0;
              const ftop = tk + (fr.length ? Math.max(...fr.map((f) => f.y + f.height)) : box?.height ?? L.height);
              const fbot = tk + (fr.length ? Math.min(...fr.map((f) => f.y)) : 0);
              const gx = X(g.x + g.width / 2);
              const gw = Math.max(g.width * sc, g.width === 0 ? 0 : 2.4);
              const ly = top - (g.row === 0 ? 52 : 22) + (gi % 2 === 1 && g.row === 1 ? -0 : 0);
              const held = g.held;
              return (
                <g key={g.key}>
                  {held && <rect x={gx - 5} y={top - 30} width={10} height={Y(fbot) - (top - 30)} fill="var(--chg)" opacity={0.45} />}
                  {gw > 0 && <rect x={gx - gw / 2} y={Y(ftop)} width={gw} height={(ftop - fbot) * sc} fill={held ? 'var(--open-line)' : 'var(--gap)'} />}
                  <line x1={gx} y1={ly + 4} x2={gx} y2={Y(ftop) - 2} stroke={held ? 'var(--open-line)' : 'var(--fg-2)'} strokeWidth={held ? 1.6 : 1} strokeDasharray={held ? undefined : '2 3'} />
                  <text x={gx} y={ly} textAnchor="middle" fontSize={held ? 11.5 : 10.5} fontWeight={held ? 700 : 400} fontFamily="IBM Plex Mono, monospace" fill={held ? 'var(--fg)' : 'var(--fg-2)'} paintOrder="stroke" stroke="var(--card)" strokeWidth={3.5} strokeLinejoin="round">
                    {g.label}
                  </text>
                  {held && g.standard !== null && (
                    <text x={gx} y={ly - 15} textAnchor="middle" fontSize={10.5} fontFamily="IBM Plex Mono, monospace" fill="var(--fg-2)" paintOrder="stroke" stroke="var(--card)" strokeWidth={3.5}>
                      standard {jointStd(L, g) ?? frac(g.standard)} · approve below
                    </text>
                  )}
                </g>
              );
            })}
            {/* top and bottom reveals, read at the right of the run */}
            {L.boxes.length > 0 &&
              (() => {
                const b = L.boxes[L.boxes.length - 1]!;
                const fr = b.fronts;
                if (!fr.length) return null;
                const tk = tkOf(b);
                const ftop = tk + Math.max(...fr.map((f) => f.y + f.height));
                const fbot = tk + Math.min(...fr.map((f) => f.y));
                const rx = X(L.width) + 8;
                return (
                  <g fontSize={10.5} fontFamily="IBM Plex Mono, monospace" fill="var(--fg-2)">
                    <line x1={rx} y1={Y(b.height)} x2={rx + 6} y2={Y(b.height)} stroke="var(--fg-2)" />
                    <line x1={rx} y1={Y(ftop)} x2={rx + 6} y2={Y(ftop)} stroke="var(--fg-2)" />
                    <text x={rx + 9} y={(Y(b.height) + Y(ftop)) / 2 + 4} fontWeight={b.reveals.top.held ? 700 : 400}>
                      top {frac(b.reveals.top.value)}
                    </text>
                    <line x1={rx} y1={Y(fbot)} x2={rx + 6} y2={Y(fbot)} stroke="var(--fg-2)" />
                    <text x={rx + 9} y={Y(fbot) + 4} fontWeight={b.reveals.bottom.held ? 700 : 400}>
                      bottom {frac(b.reveals.bottom.value)}
                    </text>
                  </g>
                );
              })()}
            {/* box widths under the run */}
            {L.boxes.map((b) => {
              const y = Y(0) + 18;
              return (
                <g key={`w-${b.item.id}`} stroke="var(--fg-2)">
                  <line x1={X(b.x)} y1={y} x2={X(b.x + b.width)} y2={y} />
                  <line x1={X(b.x)} y1={y - 5} x2={X(b.x)} y2={y + 5} />
                  <line x1={X(b.x + b.width)} y1={y - 5} x2={X(b.x + b.width)} y2={y + 5} />
                  <text x={X(b.x + b.width / 2)} y={y + 15} textAnchor="middle" fontSize={11} fontWeight={700} fontFamily="IBM Plex Mono, monospace" fill="var(--fg)" stroke="none">
                    {b.item.number} · {frac(b.width)}
                  </text>
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
}
